import React, { useCallback, useEffect, useMemo, useState } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { triggerToast } from "../../utils/toast";
import { axiosInstance } from "../../utils/axiosInstance";
import { formatFilterValue } from "../../utils/formatFilters";
import { Pagination } from "cprime-foundational-components";

type RoleComparisonAttribute = {
  attributeType?: string;
  status?: string;
  currentValue?: string;
  newValue?: string;
};

type RoleComparisonItem = {
  roleName?: string;
  roleStatus?: string;
  currentApplication?: string;
  newApplication?: string;
  attributes?: RoleComparisonAttribute[];
};

type RoleComparisonProps = {
  url?: string;
  payload?: any;
  app1Name?: string;
  app2Name?: string;
  setNewCode?: any;
  title?: string;
};

const getStatusChip = (status?: string) => {
  const normalizedStatus = (status || "").toUpperCase();
  const isMatching = normalizedStatus === "MATCHING";

  if (!normalizedStatus) return "";

  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.25rem 0.5rem",
        borderRadius: "0.25rem",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
        backgroundColor: isMatching ? "#D9EEE0" : "#F6E1E4",
        color: isMatching ? "#1F6A45" : "#8C1D2F",
      }}
    >
      {normalizedStatus}
    </span>
  );
};

const formatApplicationValue = (value?: string | boolean | null) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return value || "-";
};

const RoleComparison = ({
  app1Name = "",
  app2Name = "",
  url = "",
  payload = {},
  setNewCode,
  title = "Role Comparison",
}: RoleComparisonProps) => {
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [compareData, setCompareData] = useState<RoleComparisonItem[] | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  const parentColumns = useMemo(
    () => [
      // {
      //   name: "Role Name",
      //   id: "roleName",
      //   type: "FREETEXT",
      //   size: 450,
      // },
      {
        name: "Role Status",
        id: "roleStatus",
        type: "FREETEXT",
        size: 150,
      },
      {
        name: app1Name || "Currently Published Application",
        id: "currentApplication",
        type: "FREETEXT",
        size: app1Name ? 500 : 400,
      },
      {
        name: app2Name || "New Application",
        id: "newApplication",
        type: "FREETEXT",
        size: app2Name ? 500 : 400,
      },
    ],
    [app1Name, app2Name],
  );

  const childColumns = useMemo(
    () => [
      {
        name: "Attribute Type",
        id: "attributeType",
        type: "FREETEXT",
        size: 180,
      },
      {
        name: "Attribute Status",
        id: "attributeStatus",
        type: "FREETEXT",
        size: 180,
      },
      {
        name: "Attribute In Current Application",
        id: "currentApplication",
        type: "FREETEXT",
        size: 260,
      },
      {
        name: "Attribute In New Application",
        id: "newApplication",
        type: "FREETEXT",
        size: 260,
      },
    ],
    [],
  );

  const getCompareData = useCallback(async () => {
    try {
      setIsFetchingData(true);
      setCompareData(null);
      setTotalCount(0);
      const response = await axiosInstance.post(url, {
        ...payload,
        sortingFilter: {
          filter:
            filters?.length > 0
              ? (filters || [])?.map((item: any) =>
                  formatFilterValue(item?.value, item?.id),
                )
              : [],
          sort: sorting?.map((item: any) => {
            return {
              columnName: item?.id,
              type: item?.desc ? "DESC" : "ASC",
            };
          }),
          page: {
            limit: pagination?.rowsPerPage,
            offset: pagination?.page * pagination?.rowsPerPage,
          },
        },
      });
      setCompareData(response?.data?.data?.comparison);
      setTotalCount(response?.data?.data?.totalCount ?? 0);
      setIsFetchingData(false);
      if (setNewCode && response?.data?.data?.newJson) {
        setNewCode(response?.data?.data?.newJson);
      }
    } catch (error: any) {
      triggerToast("Error comparing versions", "error");
      setIsFetchingData(false);
    }
  }, [filters, pagination, payload, setNewCode, sorting, url]);

  useEffect(() => {
    getCompareData();
  }, [getCompareData]);

  useEffect(() => {
    setPagination((prev) => {
      // Avoid unnecessary state updates on initial mount.
      if (prev.page === 0) {
        return prev;
      }

      return {
        ...prev,
        page: 0,
      };
    });
  }, [filters]);

  const gridData: GridTableDataType | any = useMemo(() => {
    const safeCompareData = Array.isArray(compareData) ? compareData : [];

    const rows = safeCompareData.map((item, index) => ({
      id: index + 1,
      roleName: item?.roleName || "-",
      roleStatus: getStatusChip(item?.roleStatus),
      currentApplication: formatApplicationValue(item?.currentApplication),
      newApplication: formatApplicationValue(item?.newApplication),
      subRows: item?.attributes?.length
        ? [
            {
              id: `${index + 1}-details`,
              subComponents: [
                <div
                  key={`${index + 1}-details-grid`}
                  style={{ width: "100%" }}
                >
                  <GridTable
                    style={{ width: "100%" }}
                    title=""
                    data={{
                      columns: childColumns,
                      totalCount: item.attributes.length,
                      rows: item.attributes.map((attribute, subIndex) => ({
                        id: `${index + 1}-${subIndex + 1}`,
                        attributeType: attribute?.attributeType || "-",
                        attributeStatus: getStatusChip(attribute?.status),
                        currentApplication: formatApplicationValue(
                          attribute?.currentValue,
                        ),
                        newApplication: formatApplicationValue(
                          attribute?.newValue,
                        ),
                      })),
                    }}
                    isCustomizeColumns={false}
                    isFiltering={false}
                    isSorting={false}
                    isPagination={false}
                    showHeader={false}
                    loading={false}
                  />
                </div>,
              ],
            },
          ]
        : [],
    }));

    return {
      columns: parentColumns,
      totalCount,
      rows,
    };
  }, [childColumns, compareData, parentColumns, totalCount]);

  return (
    <>
      <GridTable
        title={title}
        style={{ minHeight: "10rem" }}
        data={gridData}
        isFiltering={true}
        isSorting={true}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        pagination={pagination}
        isPagination={true}
        loading={isFetchingData}
        showHeader={true}
      />
      <Pagination
        sx={{ width: "100%" }}
        {...{
          ...pagination,
          count: totalCount ?? 0,
          rowsPerPageOptions: [25, 50, 100, 250],
          onPageChange: (_e, number) =>
            setPagination({ ...pagination, page: number }),
          onRowsPerPageChange: (evt) =>
            setPagination({
              page: 0,
              rowsPerPage: Number(evt.target.value ?? 25),
            }),
        }}
      />
    </>
  );
};

export default React.memo(RoleComparison, (prevProps, nextProps) => {
  const isApp1NameUnchanged = prevProps.app1Name === nextProps.app1Name;
  const isApp2NameUnchanged = prevProps.app2Name === nextProps.app2Name;
  const isUrlUnchanged = prevProps.url === nextProps.url;
  const isPayloadUnchanged =
    JSON.stringify(prevProps.payload) === JSON.stringify(nextProps.payload);
  const isSetNewCodeUnchanged = prevProps.setNewCode === nextProps.setNewCode;
  const isTitleUnchanged = prevProps.title === nextProps.title;

  const isLoadingUnchanged =
    isApp1NameUnchanged &&
    isApp2NameUnchanged &&
    isUrlUnchanged &&
    isPayloadUnchanged &&
    isSetNewCodeUnchanged &&
    isTitleUnchanged;

  return isLoadingUnchanged;
});
